import { ChevronsDownUp, ChevronsUpDown, ListTree, Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { CategoryTree } from '@/components/CategoryTree/CategoryTree'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { PageHeader } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { CategoryFormModal } from '@/forms/CategoryForm/CategoryFormModal'
import { Category } from '@/interfaces/catalog/Category'
import { Product } from '@/interfaces/catalog/Product'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatCount } from '@/lib/catalog/catalogTypes'

export default function CatalogCategoriesPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog',
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | undefined>()
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Category | undefined>()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsedState] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('invo.categories.collapsed') ?? '[]'))
    } catch {
      return new Set()
    }
  })
  const setCollapsed = (next: Set<string>) => {
    setCollapsedState(next)
    localStorage.setItem('invo.categories.collapsed', JSON.stringify(Array.from(next)))
  }

  const {
    data: categoriesResponse,
    isLoading,
    mutate,
  } = useSWR<{ data?: Category[]; total?: number }>(
    '/api/v2/categories',
    apiFetcher,
  )

  const { data: productsResponse } = useSWR<{ data?: Product[]; total?: number }>('/api/v2/products', apiFetcher)

  const categories = categoriesResponse?.data || []

  const productCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const product of productsResponse?.data ?? []) {
      if (product.mainCategoryId) map.set(product.mainCategoryId, (map.get(product.mainCategoryId) ?? 0) + 1)
    }
    return map
  }, [productsResponse])

  const parentIds = useMemo(() => {
    const ids: string[] = []
    const walk = (list: Category[]) =>
      list.forEach((c) => {
        if (c.children?.length) {
          ids.push(c.id)
          walk(c.children)
        }
      })
    walk(categories)
    return ids
  }, [categories])
  const allCollapsed = parentIds.length > 0 && parentIds.every((id) => collapsed.has(id))

  const categoryCount = useMemo(() => {
    const count = (list: Category[]): number => list.reduce((acc, c) => acc + 1 + count(c.children ?? []), 0)
    return count(categories)
  }, [categories])

  const handleCreate = () => {
    setEditingCategory(undefined)
    setDefaultParentId(null)
    setIsModalOpen(true)
  }

  const handleAddSubcategory = (category: Category) => {
    setEditingCategory(undefined)
    setDefaultParentId(category.id)
    setIsModalOpen(true)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setIsModalOpen(true)
  }

  const handleDelete = (category: Category) => {
    setDeletingCategory(category)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deletingCategory) {
      return Promise.resolve()
    }

    try {
      await API.delete(`/api/v2/categories/${deletingCategory.id}`)
      toast.success(t('categories.delete.success'))
      mutate()
      setIsDeleteDialogOpen(false)
      setDeletingCategory(undefined)
    } catch (error: unknown) {
      console.error('Delete error:', error)
      const errorMessage = extractErrorMessage(error)
      toast.error(t('categories.delete.error'), {
        description: errorMessage || t('categories.error.description'),
      })
      throw error
    }
  }

  const handleModalSuccess = () => {
    mutate()
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingCategory(undefined)
    setDefaultParentId(null)
  }

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/catalog',
        children: t('title'),
      },
      {
        href: '/catalog/categories',
        children: t('categories.title'),
      },
    ],
  })

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <title>{t('categories.title')}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={t('categories.title')}
          description={
            categories.length
              ? `${formatCount(categoryCount, 'kategorie', 'kategorie', 'kategorií')}. Třídí produkty v katalogu, pokladně a e-shopu.`
              : undefined
          }
          actions={
            <Button onClick={handleCreate}>
              <Plus />
              {t('categories.actions.create')}
            </Button>
          }
        />

        {categories.length > 0 ? (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="relative w-full max-w-xs">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Hledat kategorii"
                  aria-label="Hledat kategorii"
                  className="h-8 pl-8"
                />
              </div>
              {parentIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-muted-foreground"
                  onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(parentIds))}
                >
                  {allCollapsed ? <ChevronsUpDown /> : <ChevronsDownUp />}
                  {allCollapsed ? 'Rozbalit vše' : 'Sbalit vše'}
                </Button>
              )}
            </div>
            <div className="rounded-lg border p-1.5">
              <CategoryTree
                categories={categories}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddSubcategory={handleAddSubcategory}
                onPositionsUpdate={handleModalSuccess}
                productCounts={productsResponse ? productCounts : undefined}
                query={query}
                collapsed={collapsed}
                onCollapsedChange={setCollapsed}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Podkategorie přesunete za úchyt vlevo, posunem doprava je zanoříte. Hlavní kategorie zatím přesouvat nejde.
            </p>
          </div>
        ) : (
          <EmptyState icon={ListTree}
            title={t('categories.empty')}
            description="Kategorie třídí produkty v katalogu, v pokladně i v e-shopu."
            ctaButton={<Button onClick={handleCreate}>{t('categories.actions.create')}</Button>}
          />
        )}
      </div>

      <CategoryFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        category={editingCategory}
        defaultParentId={defaultParentId}
        categories={categories}
        onSuccess={handleModalSuccess}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title={t('categories.delete.confirm.title', {
          name: deletingCategory?.name,
        })}
        description={t('categories.delete.confirm.description')}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}
